import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, AlertCircle, PauseCircle, Split } from 'lucide-react';
import { purchasingAPI, PurchaseOrderLine } from '../../lib/api';
import type { Job } from '../../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ChangelogEntry } from '../common/Changelog';
import { mergeAuditHistory } from './jobAuditEntries';
import { AcceptDateModal, DeclineDateModal, ProposeChangeModal } from './AcknowledgmentWorkflowModals';
import { HoldReasonModal } from './HoldReasonModal';
import POLineGRNTab from './POLineGRNTab';
import POLineASNTab from './POLineASNTab';
import POLineJobStatusTab from './POLineJobStatusTab';
import POLineDocumentsTab from './POLineDocumentsTab';
import ShortageManagement from './ShortageManagement';
import POLineNCRTab, { POLineNCRTabTrigger } from './POLineNCRTab';
import CommunicationLog from './CommunicationLog';
import ChangeRequestsTab from './ChangeRequestsTab';
import { POLineDetailTabContent } from './POLineDetailTab';
import { POLineDetailFooter } from './POLineDetailFooter';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useUserPermissions } from '../../contexts/UserPermissionsContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';

interface POLineDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineId: number;
  userType?: 'supplier' | 'customer';
  initialAction?: 'propose_change' | null;
  initialTab?: string;
  onUpdate?: () => void;
}

export default function POLineDetailModal({ isOpen, onClose, lineId, userType = 'supplier', initialAction = null, initialTab, onUpdate }: POLineDetailModalProps) {
  const { user } = useAuth();
  const { hasPurchasingWriteAccess } = useUserPermissions();
  const { isFeatureEnabled, hideCommunicationBox } = useMenuSettings();
  // When the tenant uses schedule-line collaboration, PO-line-level
  // Accept / Decline / Propose are suppressed — demand lives on
  // POLineSchedule rows and negotiation belongs in the Schedule modal.
  // Keeping them visible here meant DE users could propose changes
  // against the PO-line header that never flowed back to any schedule,
  // creating two disconnected audit trails for the same logical ask.
  const scheduleCollaborationActive = isFeatureEnabled('po_line_schedules');
  // Certificate / despatch note are gated in Admin Setup -> Features and the
  // backend refuses to generate them when off, so hide the tab to match.
  const jobDocumentsActive = isFeatureEnabled('job_documents');
  const [line, setLine] = useState<PurchaseOrderLine | null>(null);
  const [changelogs, setChangelogs] = useState<ChangelogEntry[]>([]);
  // Job events are merged into the audit history but deliberately kept out
  // of `changelogs`: the change-request tab and the Job Status fallback both
  // filter that list by field_changed and would misread synthetic rows.
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // GRN count was used for the GRN tab label badge — tab is now hidden,
  // but we still call the API and discard the value to keep the existing
  // Promise.all wiring intact.

  // Workflow modal state
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [proposeModalOpen, setProposeModalOpen] = useState(false);
  const [holdModalOpen, setHoldModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState(initialTab || 'detail');

  useEffect(() => {
    if (isOpen && lineId) {
      fetchLineDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lineId]);

  // CR-3400: Auto-open propose change modal when initialAction is set.
  // CR-4680 round 6: must only fire once per mount. Without the ref guard the
  // effect re-fires whenever `line` updates (e.g. fetchLineDetails reloading
  // after a cancel-change call), which re-opens the Propose Change modal
  // after the user cancels a change request. The parent unmounts this modal
  // on close (selectedLineId → null), so the ref resets naturally.
  const proposeAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (line && initialAction === 'propose_change' && !proposeAutoOpenedRef.current) {
      proposeAutoOpenedRef.current = true;
      setProposeModalOpen(true);
    }
  }, [line, initialAction]);

  interface RawChangelog {
    id: number; change_date: string; field_changed: string; old_value: string; new_value: string;
    changed_by_details?: { full_name?: string; companyName?: string };
    reason?: string; change_type?: string; event_type?: string;
    // CR-4712 round 2 rework: previous version of this transformer read
    // `changed_by_company_name` (with the _name suffix) and
    // `changed_by_details.companyName` — neither field exists on the
    // backend payload. The serializer returns `changed_by_company`
    // (resolved string from get_changed_by_company) directly. The
    // mismatched key meant this transformer always produced
    // `changed_by_company: undefined`, the Changelog row renderer fell
    // through its `entry.changed_by_company ? ... : ''` ternary, and
    // the audit row rendered as a bare actor name in production —
    // exactly the regression Sarah reported. Read the actual field.
    changed_by_company?: string;
    // CR-4713 round 2 rework: surface the impersonation actor + their
    // company so the Changelog row renderer can append the
    // "(acting as <Company> · <Name>)" suffix.
    acting_as_user_details?: { id: number; first_name?: string; last_name?: string; full_name?: string; email?: string } | null;
    acting_as_company?: string | null;
    // CR-4865: carry the change-request grouping key through to the tab renderer.
    change_request_ref?: number | null;
    po_line?: number | null;
    // CR-4713 round 4: the renderer needs this to say "on behalf of customer/supplier".
    company_type?: 'supplier' | 'customer' | '';
  }
  const transformChangelogs = (results: RawChangelog[]): ChangelogEntry[] =>
    results.map((log) => ({
      id: log.id, change_date: log.change_date,
      changed_by: log.changed_by_details?.full_name || 'System',
      changed_by_company: log.changed_by_company,
      acting_as_user_details: log.acting_as_user_details,
      acting_as_company: log.acting_as_company,
      field_changed: log.field_changed, old_value: log.old_value, new_value: log.new_value,
      reason: log.reason, change_type: log.change_type, event_type: log.event_type,
      change_request_ref: log.change_request_ref ?? null,
      company_type: log.company_type,
    }));

  const fetchLineDetails = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const [lineResponse, changelogResponse, grnResponse, jobsResponse] = await Promise.all([
        purchasingAPI.getLine(lineId),
        // page_size: the API defaults to 20 rows, which silently truncated the
        // audit history and made the per-field change counts undercount — a
        // line with seven promise-date moves showed a badge of 1 because the
        // other six sat on page 2. 100 is the API's ceiling.
        purchasingAPI.getChangelogs({ po_line: lineId, ordering: '-change_date', page_size: 100 }),
        purchasingAPI.getLineGRNSummary(lineId),
        // Never fatal: a line with no imported jobs still has an audit history.
        purchasingAPI.getPOLineJobs(lineId).catch(() => null),
      ]);

      setLine(lineResponse.data);
      setChangelogs(transformChangelogs(changelogResponse.data.results));
      setJobs(jobsResponse ? jobsResponse.data.results : null);
      void grnResponse;
    } catch (err: unknown) {
      console.error('Failed to fetch line details:', err);
      if (!silent) {
        const axiosError = err as { response?: { data?: { detail?: string } } };
        setError(axiosError.response?.data?.detail || 'Failed to load line details');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const { formatCurrency: contextFormatCurrency, formatDate: contextFormatDate } = useFormatters();

  const formatCurrency = (value: string, currency?: string) => {
    const num = parseFloat(value);
    return contextFormatCurrency(num, currency, true);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return contextFormatDate(dateString);
  };

  // Both documents are generated from imported job records, so the tab is
  // only offered when there are any. A line still on the change-log fallback
  // has nothing to print and the endpoint would return a 400 it cannot act on.
  const hasJobs = !!jobs && jobs.length > 0;

  // A split line's work runs as several sub-jobs, each with its own promise
  // date — the single date on the header hides that, so say so up front.
  // Counted only when the ERP flagged a split: an unsplit line still has one
  // job record, and "1 sub-job" is noise.
  const splitJobCount = useMemo(
    () => (jobs?.some(job => job.is_split) ? jobs.length : 0),
    [jobs],
  );

  // The line's own audit rows and the ERP's job events as one timeline.
  const auditHistory = useMemo(
    () => mergeAuditHistory(changelogs, jobs),
    [changelogs, jobs],
  );

  const getChangeCount = (fieldName: string) => {
    // CR-4731 round 4: a change-request lifecycle writes both a propose row
    // and a decision row on the same field (post-CR-4865 they share a
    // change_request_ref; pre-CR-4865 legacy rows don't). A naive .length
    // double-counts. Group by ref for modern rows; for legacy rows without a
    // ref, skip ones that look like decisions so a propose + decision pair
    // still resolves to one change.
    const isDecisionRow = (reason?: string | null) =>
      /\b(accepted|declined|rejected|cancelled)\b/i.test(reason || '');
    const seenRefs = new Set<number>();
    let count = 0;
    changelogs.forEach(log => {
      if (log.field_changed !== fieldName) return;
      if (log.change_request_ref != null) {
        if (!seenRefs.has(log.change_request_ref)) {
          seenRefs.add(log.change_request_ref);
          count++;
        }
      } else if (!isDecisionRow(log.reason)) {
        count++;
      }
    });
    return count > 0 ? count : null;
  };

  const handleToggleCritical = async () => {
    if (!line) return;
    try {
      const newCriticalState = !line.is_critical;
      const response = await purchasingAPI.setLineCritical(line.id, newCriticalState);
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setLine(prev => prev ? { ...prev, is_critical: newCriticalState } : null);
        const changelogResponse = await purchasingAPI.getChangelogs({ po_line: lineId, ordering: '-change_date' });
        setChangelogs(transformChangelogs(changelogResponse.data.results));
        onUpdate?.();
      }
    } catch (error: unknown) {
      console.error('Failed to toggle critical:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update critical status';
      toast.error(errorMessage);
    }
  };

  const handleToggleHold = async (reason?: string) => {
    if (!line) return;
    try {
      let response;
      if (line.on_hold) {
        response = await purchasingAPI.releaseLineHold(line.id);
      } else {
        if (!reason) return;
        response = await purchasingAPI.putLineOnHold(line.id, { reason });
      }
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        await fetchLineDetails();
        onUpdate?.();
      }
    } catch (err: unknown) {
      console.error('Failed to toggle hold:', err);
      const axiosError = err as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Failed to update hold status');
    }
  };

  const handleWorkflowAction = async (data: {
    action: string;
    lineId: number;
    comments?: string;
    reason?: string;
    proposedDate?: string;
    proposedQuantity?: number;
    proposedPrice?: number;
    files?: File[];
  }) => {
    try {
      let response;

      switch (data.action) {
        case 'accept':
          response = await purchasingAPI.acceptDeliveryDate(data.lineId, { comments: data.comments });
          break;
        case 'decline':
          response = await purchasingAPI.declineDeliveryDate(data.lineId, { reason: data.reason ?? '', comments: data.comments });
          break;
        case 'propose':
          response = await purchasingAPI.proposeAlternativeDate(data.lineId, { proposedDate: data.proposedDate ?? '', reason: data.reason ?? '', comments: data.comments });
          break;
        case 'proposeQuantity':
          response = await purchasingAPI.proposeQuantityChange(data.lineId, { proposedQuantity: String(data.proposedQuantity ?? 0), reason: data.reason ?? '', comments: data.comments });
          break;
        case 'proposePrice':
          response = await purchasingAPI.proposePriceChange(data.lineId, { proposedPrice: String(data.proposedPrice ?? 0), reason: data.reason ?? '', comments: data.comments });
          break;
        default:
          throw new Error(`Unknown action: ${data.action}`);
      }

      if (response?.data?.message) {
        toast.success(response.data.message);
      }

      // CR-4723: Upload attached files as PO documents after action succeeds.
      // Use other_customer / other_supplier — PODocument has no change_request choice.
      if (data.files && data.files.length > 0 && line) {
        const docType = userType === 'customer' ? 'other_customer' : 'other_supplier';
        let failedCount = 0;
        let firstMessage: string | null = null;
        for (const file of data.files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('po', String(line.po));
          formData.append('po_line', String(line.id));
          formData.append('document_type', docType);
          formData.append(
            'description',
            `Change request attachment (${data.action}): ${file.name}`,
          );
          try {
            await purchasingAPI.uploadPODocument(formData);
          } catch (uploadErr: unknown) {
            failedCount += 1;
            console.error('Failed to upload attachment:', uploadErr);
            if (!firstMessage) {
              firstMessage = getApiErrorMessage(uploadErr, 'Upload failed');
            }
          }
        }
        if (failedCount > 0) {
          toast.error(
            failedCount === data.files.length
              ? `Attachments could not be uploaded: ${firstMessage ?? 'Unknown error'}`
              : `${failedCount} of ${data.files.length} attachments failed. ${firstMessage ?? ''}`,
          );
        }
      }

    } catch (error: unknown) {
      console.error('Workflow action failed:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      const errorMessage = axiosError.response?.data?.message || 'Workflow action failed';
      toast.error(errorMessage);
      throw error;
    }

    // CR-4860: Side effects must not propagate to the caller. Previously,
    // fetchLineDetails / onUpdate ran inside the try block above; if the
    // action API succeeded (success toast already fired) but the post-call
    // refetch errored, the throw bubbled up to the caller. ProposeChangeModal
    // would catch it and skip its own onClose(), leaving the modal open
    // even though the propose itself succeeded. Run the refresh outside
    // the try, swallow refetch errors, so the propose flow can close.
    // CR-4883: also swallow errors from the parent's onUpdate callback —
    // if onUpdate throws (e.g. parent re-render quirk), the throw used to
    // bubble back to ProposeChangeModal and skip its own onClose, so the
    // modal stayed open even though the propose itself succeeded.
    try {
      await fetchLineDetails();
    } catch (refetchErr) {
      console.warn('Workflow action succeeded but refetch failed:', refetchErr);
    }
    try {
      onUpdate?.();
    } catch (updateErr) {
      console.warn('Workflow action succeeded but onUpdate failed:', updateErr);
    }
  };

  // CR-4203: Check if workflow actions should be shown
  const canShowWorkflowActions = (): boolean => {
    if (!line || !user || !user.platform) return false;
    // CR-4697: No actions on closed (fully received) PO lines
    if (line.status === 'received') return false;
    // Schedule-aware tenants negotiate at the schedule level only.
    if (scheduleCollaborationActive) return false;

    const status = line.acknowledge_status;
    const isSupplier = user.platform === 'SUPPLIER' || user.platform === 'HELIX';
    const isCustomer = user.platform === 'CUSTOMER';

    // CR-4647: Site-based write access only applies to supplier users.
    // Customers always have write access to their own PO lines.
    if (isSupplier && !hasPurchasingWriteAccess(line.supplier_id)) return false;

    if (isSupplier) {
      switch (status) {
        case 'change_proposed_customer':
          return true;
        case 'change_declined':
          return line.previous_acknowledge_status === 'change_proposed_customer';
        default:
          return false;
      }
    }

    if (isCustomer) {
      switch (status) {
        case 'new':
        case 'change_proposed_supplier':
          return true;
        case 'change_declined':
          return line.previous_acknowledge_status === 'change_proposed_supplier';
        default:
          return false;
      }
    }

    return false;
  };

  // CR-4203: Check if propose change should be shown
  const canShowProposeChange = (): boolean => {
    if (!line || !user || !user.platform) return false;
    // CR-4697: No actions on closed (fully received) PO lines
    if (line.status === 'received') return false;
    // Schedule-aware tenants negotiate at the schedule level only.
    if (scheduleCollaborationActive) return false;

    const status = line.acknowledge_status;
    const isSupplier = user.platform === 'SUPPLIER' || user.platform === 'HELIX';
    const isCustomer = user.platform === 'CUSTOMER';

    // CR-4647: Site-based write access only applies to supplier users.
    if (isSupplier && !hasPurchasingWriteAccess(line.supplier_id)) return false;

    if (isSupplier) {
      switch (status) {
        case 'new':
        case 'accepted':
        case 'wip':
          return true;
        case 'change_declined':
          return line.previous_acknowledge_status === 'change_proposed_supplier';
        default:
          return false;
      }
    }

    if (isCustomer) {
      switch (status) {
        case 'new':
        case 'accepted':
        case 'wip':
          return true;
        case 'change_declined':
          return line.previous_acknowledge_status === 'change_proposed_customer';
        default:
          return false;
      }
    }

    return false;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-h-[95vh] flex flex-col" style={{ maxWidth: '1400px', minWidth: '1200px' }}>
        {/* Header — CR-4703: status badges only, actions moved to fixed footer */}
        <div className="flex items-center justify-between p-5 border-b bg-muted/30">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-primary">
              PO: {line?.po_number} / Line: {line?.line_number}
            </h2>
            {/* CR-4701: % Delivered in header */}
            {line && (() => {
              const qty = parseFloat(line.quantity) || 0;
              const recv = parseFloat(line.received_qty) || 0;
              const pct = qty > 0 ? Math.min(Math.round((recv / qty) * 100), 100) : 0;
              const color = pct >= 100 ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : pct >= 50 ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-gray-100 text-gray-800 border-gray-300';
              return (
                <span className={`px-3 py-1 rounded-full font-semibold text-xs border ${color}`}>
                  {pct}% Delivered
                </span>
              );
            })()}
            {line?.is_critical && (
              <span className="px-3 py-1 rounded-full font-semibold text-xs bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" />
                CRITICAL
              </span>
            )}
            {line?.on_hold && (
              <span className="px-3 py-1 rounded-full font-semibold text-xs bg-red-100 text-red-800 border border-red-300 flex items-center gap-1">
                <PauseCircle className="h-3.5 w-3.5" />
                On Hold
                {(getChangeCount('on_hold') || 0) > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-red-200 text-red-900 rounded-full text-[10px]">{getChangeCount('on_hold')}</span>
                )}
              </span>
            )}
            {splitJobCount > 0 && (
              <button
                type="button"
                onClick={() => setModalTab('job-status')}
                title="Split across sub-jobs — open Job Status"
                className="px-3 py-1 rounded-full font-semibold text-xs bg-sky-100 text-sky-800 border border-sky-300 flex items-center gap-1 hover:bg-sky-200 transition-colors"
              >
                <Split className="h-3.5 w-3.5" />
                Split &middot; {splitJobCount} sub-jobs
              </button>
            )}
            {/* CR-4704: Acknowledge status badge in header.
                CR-4859: Map 'accepted' to the WIP label so the header
                matches the PO Lines table column, where types.ts already
                renders both wip and accepted as "WIP" — the line has
                progressed past the acknowledgement phase, the change
                request itself is the only thing 'accepted'.
                CR-4882: Same stuck-state guard as the action-suppliership
                badge above — a change_proposed_* status with no actual
                proposed values renders as WIP rather than "Changes Pending". */}
            {line?.acknowledge_status && line.acknowledge_status !== 'new' && (() => {
              const status = line.acknowledge_status;
              const hasProposedValues = !!(
                line.customer_proposed_date ||
                line.customer_proposed_qty ||
                line.customer_proposed_unit_price
              );
              const effectiveStatus = status.startsWith('change_proposed') && !hasProposedValues
                ? 'accepted'
                : status;
              return (
                <span className={`px-3 py-1 rounded-full font-semibold text-xs flex items-center gap-1 ${
                  effectiveStatus === 'accepted' || effectiveStatus === 'wip' ? 'bg-green-100 text-green-800 border border-green-300' :
                  effectiveStatus.startsWith('change_proposed') ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                  effectiveStatus === 'change_declined' ? 'bg-red-100 text-red-800 border border-red-300' :
                  'bg-gray-100 text-gray-800 border border-gray-300'
                }`}>
                  {effectiveStatus === 'accepted' || effectiveStatus === 'wip' ? 'WIP' :
                   effectiveStatus.startsWith('change_proposed') ? 'Changes Pending' :
                   effectiveStatus === 'change_declined' ? 'Change Declined' :
                   effectiveStatus}
                </span>
              );
            })()}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
              <p className="text-red-800">Error: {error}</p>
              <button onClick={() => fetchLineDetails()} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">Try again</button>
            </div>
          ) : line ? (
            <Tabs value={modalTab} onValueChange={setModalTab} className="w-full">
              <div className="border-b px-4 pt-2">
                <TabsList className="bg-transparent border-none">
                  <TabsTrigger value="detail" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">Detail</TabsTrigger>
                  <TabsTrigger value="job-status" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">Job Status</TabsTrigger>
                  <POLineNCRTabTrigger lineId={lineId} />{/* shown only when the ERP recorded rejects */}
                  {hasJobs && jobDocumentsActive && (
                    <TabsTrigger value="documents" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">Documents</TabsTrigger>
                  )}
                  <TabsTrigger value="asn" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">ASN/Shipments</TabsTrigger>
                  {!hideCommunicationBox && (
                    <TabsTrigger value="communication" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">Communication</TabsTrigger>
                  )}
                  {userType === 'supplier' && line?.shortage_qty && (
                    <TabsTrigger value="shortage" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">
                      <span className="flex items-center gap-2">
                        Shortage
                        {!line.shortage_resolved && <span className="bg-red-500 text-white text-xs font-semibold px-1.5 py-0.5 rounded">!</span>}
                      </span>
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>

              <TabsContent value="detail" className="m-0 p-4">
                <POLineDetailTabContent
                  line={line}
                  changelogs={auditHistory}
                  userType={userType}
                  userId={user?.id}
                  formatCurrency={formatCurrency}
                  formatDate={formatDate}
                  getChangeCount={getChangeCount}
                  canShowWorkflowActions={canShowWorkflowActions()}
                  onReviewChanges={() => setModalTab('change-requests')}
                />
              </TabsContent>

              <TabsContent value="job-status" className="m-0 p-4">
                <POLineJobStatusTab changelogs={changelogs} line={line} />
              </TabsContent>
              {/* Same gate as the tab trigger: a deep link (lineTab=documents) to a
                  line without jobs must not render the tab's content headless. */}
              {hasJobs && jobDocumentsActive && (
                <TabsContent value="documents" className="m-0 p-6">
                  {line && <POLineDocumentsTab lineId={line.id} />}
                </TabsContent>
              )}
              <TabsContent value="grn" className="m-0 p-6"><POLineGRNTab lineId={lineId} orderedQty={line ? parseFloat(line.quantity) : 0} /></TabsContent>
              <TabsContent value="asn" className="m-0 p-6"><POLineASNTab lineId={lineId} /></TabsContent>
              {line?.shortage_qty && (
                <TabsContent value="shortage" className="m-0 p-6"><ShortageManagement line={line} onUpdate={fetchLineDetails} /></TabsContent>
              )}
              <TabsContent value="ncr" className="m-0 p-6"><POLineNCRTab lineId={lineId} /></TabsContent>
              <TabsContent value="change-requests" className="m-0 p-6">
                <ChangeRequestsTab line={line} changelogs={changelogs} userType={userType} onAccept={() => setAcceptModalOpen(true)} onDecline={() => setDeclineModalOpen(true)} onUpdate={async () => { await fetchLineDetails(); onUpdate?.(); }} />
              </TabsContent>
              {!hideCommunicationBox && (
                <TabsContent value="communication" className="m-0 p-6">
                  <CommunicationLog lineId={lineId} poId={line.po} partNumber={line.part_number} />
                </TabsContent>
              )}
            </Tabs>
          ) : null}
        </div>

        {/* Footer — CR-4703: fixed action bar, always visible.
            CR-4705 round 2 rework: pass activeTab so the footer can hide
            the "Review Changes" button once the user is already on the
            Change Requests tab. */}
        {line && (
          <POLineDetailFooter
            line={line}
            activeTab={modalTab}
            canToggleCritical={line.status !== 'received' && userType !== 'customer'}
            canToggleHold={line.status !== 'received' && hasPurchasingWriteAccess(line.supplier_id)}
            canShowProposeChange={canShowProposeChange()}
            canShowWorkflowActions={canShowWorkflowActions()}
            isProposer={
              line.status !== 'received' && (
                (userType === 'customer' && line.acknowledge_status === 'change_proposed_customer') ||
                (userType === 'supplier' && line.acknowledge_status === 'change_proposed_supplier')
              )
            }
            onToggleCritical={handleToggleCritical}
            onToggleHold={() => {
              if (line.on_hold) {
                handleToggleHold();
              } else {
                setHoldModalOpen(true);
              }
            }}
            onPropose={() => setProposeModalOpen(true)}
            onSwitchToChangeRequests={() => setModalTab('change-requests')}
            onClose={onClose}
          />
        )}
        {!line && (
          <div className="border-t p-4 flex justify-end gap-3 bg-muted/10">
            <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg hover:bg-muted font-medium">Close</button>
          </div>
        )}
      </div>

      {/* Workflow Modals */}
      {line && (
        <>
          {/* CR-4729 rework: pass the customer-proposed promise date so the
              modal can render "From <delivery_date> to <proposedDate>"
              instead of just the existing date. */}
          <AcceptDateModal isOpen={acceptModalOpen} onClose={() => setAcceptModalOpen(false)} lineId={line.id} lineNumber={line.line_number} partNumber={line.part_number} orderNumber={line.po_number} currentDeliveryDate={line.delivery_date} proposedDate={line.customer_proposed_date || line.supplier_promise_date || undefined} onSubmit={handleWorkflowAction} />
          <DeclineDateModal isOpen={declineModalOpen} onClose={() => setDeclineModalOpen(false)} lineId={line.id} lineNumber={line.line_number} partNumber={line.part_number} orderNumber={line.po_number} currentDeliveryDate={line.delivery_date} proposedDate={line.customer_proposed_date || line.supplier_promise_date || undefined} onSubmit={handleWorkflowAction} />
          <ProposeChangeModal isOpen={proposeModalOpen} onClose={() => setProposeModalOpen(false)} lineId={line.id} lineNumber={line.line_number} partNumber={line.part_number} orderNumber={line.po_number} currentDeliveryDate={line.delivery_date} currentQuantity={line.quantity} currentUnitPrice={line.unit_price} onSubmit={handleWorkflowAction} userType={userType} />
          <HoldReasonModal
            isOpen={holdModalOpen}
            onClose={() => setHoldModalOpen(false)}
            onSubmit={async (reason: string) => {
              await handleToggleHold(reason);
            }}
            lineNumber={line.line_number}
            partNumber={line.part_number}
          />
        </>
      )}
    </div>
  );
}
